import { useState, useEffect } from "react";
import "./DocumentsDashboard.css";
import axios from "axios";

const DocumentsDashboard = ({ sharepointDocs = [] }) => {
  const [activeTab, setActiveTab] = useState("user");
  const [userDocs, setUserDocs] = useState([]);

  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  axios.defaults.adapter = "xhr";

  useEffect(() => {
    const loadDocs = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/docs/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setUserDocs(response.data);
      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    };
    loadDocs();
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
      e.dataTransfer.clearData();
    }
  };

  const handleFileSelect = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const VECTOR_DB_URL = "https://n8n.three60energy.group/webhook/tender-delete"; // TODO: replace with actual URL

  const handleDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.fileName}"?`))
      return;
    try {
      await Promise.all([
        axios.delete(`${process.env.REACT_APP_API_URL}/docs/${doc.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
        axios.post(`${VECTOR_DB_URL}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          data: { fileName: doc.fileName },
        }),
      ]);
      setUserDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  function formatDate(timestamp, locale = "en-GB") {
    if (!timestamp) return "";

    // Convert backend timestamp to ISO 8601
    const isoString = timestamp.replace(" ", "T");

    const date = new Date(isoString);

    if (isNaN(date.getTime())) return "";

    return date.toLocaleString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  const handleSubmit = () => {
    if (!files.length) return;

    console.log("Submitting files:", files);

    const apiURL = "https://n8n.three60energy.group/webhook/tender-upload";

    const formData = new FormData();

    // Add files as binary data
    if (files && files.length > 0) {
      files.forEach((file, index) => {
        let type = file.type;

        if (file.name.toLowerCase().endsWith(".zip")) {
          type = "application/zip";
        }

        const fixedFile = new File([file], file.name, { type });
        formData.append(`file${index}`, fixedFile);
      });
    }

    setUploading(true);

    axios
      .post(apiURL, formData, {
        headers: {
          Authorization: `Bearer Secure`,
        },
        onUploadProgress: (e) => {
          if (!e.total) return;

          const percent = Math.round((e.loaded * 100) / e.total);
          setProgress((p) => Math.max(p, Math.min(percent, 95)));
        },
      })
      .then(async (response) => {
        console.log("Files uploaded successfully:", response.data);
        setProgress(100);

        setTimeout(() => {
          setUploading(false);
          setFiles([]);
          setProgress(0);
        }, 600);
        const newUserDocs = response.data.filter(
          (doc) => !doc.source || doc.source === "user",
        );
        if (newUserDocs.length > 0) {
          setUserDocs((prev) => [...prev, ...newUserDocs]);
        }
      })
      .catch((error) => {
        console.error("Error uploading files:", error);
        setProgress(0);
        setUploading(false);
      });
  };

  const renderTable = (documents, onDelete = null) => (
    <table className="documents-table">
      <thead>
        <tr>
          <th>Document Name</th>
          <th>Document Size</th>
          <th>Upload Time</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {documents.length > 0 ? (
          documents.map((doc) => (
            <tr key={doc.id}>
              <td>{doc.fileName}</td>
              <td>{doc.fileSize}</td>
              <td>{formatDate(doc.uploaded_at)}</td>
              <td>
                <div className="action-buttons">
                  {onDelete && (
                    <button
                      className="delete-button"
                      onClick={() => onDelete(doc)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                      Delete
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="4" className="no-documents">
              No documents found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="admin-content">
      {/* Tabs Header */}
      <div className="tabs-header">
        <button
          className={`tab-button ${activeTab === "user" ? "active" : ""}`}
          onClick={() => setActiveTab("user")}
        >
          User Uploaded Documents
        </button>

        <button
          className={`tab-button ${activeTab === "sharepoint" ? "active" : ""}`}
          onClick={() => setActiveTab("sharepoint")}
        >
          SharePoint Uploaded Documents
        </button>
      </div>

      {/* Tabs Content */}
      <div className="tabs-content">
        {activeTab === "user" && (
          <>
            <div className="documents-table-wrapper">
              {renderTable(userDocs, handleDelete)}
            </div>

            {/* Upload Placeholder */}
            <div className="upload-area">
              <input
                type="file"
                id="fileUpload"
                multiple
                hidden
                onChange={handleFileSelect}
              />

              <label
                htmlFor="fileUpload"
                className={`upload-box ${dragActive ? "drag-active" : ""}`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
              >
                <div className="upload-icon">⬆</div>
                <p>
                  Drag & drop documents here
                  <br />
                  or <span className="upload-link">browse files</span>
                </p>
              </label>

              {files.length > 0 && (
                <div className="file-preview-docs">
                  <h4>Files to upload</h4>

                  <ul>
                    {files.map((file, index) => (
                      <li key={index}>
                        <span>{file.name}</span>
                        <span className="file-size">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                        <button onClick={() => removeFile(index)}>✕</button>
                      </li>
                    ))}
                  </ul>

                  {uploading && (
                    <div className="progress-loader">
                      <div
                        className="progress"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  )}

                  <button className="upload-submit" onClick={handleSubmit}>
                    Upload {files.length} file{files.length > 1 ? "s" : ""}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "sharepoint" && (
          <div className="documents-table-wrapper">
            {renderTable(sharepointDocs)}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsDashboard;
